/**
 * Per-category details for the Translations Tab coverage strip.
 * Opened by clicking "View details". Four tabs — one per diff bucket —
 * each lists the affected keys with their EN values.
 *
 * The Mismatches tab is interactive: each row offers "Keep local"
 * (push the local EN up to UILM) and "Keep UILM" (pull UILM's EN down
 * to local). The other three tabs stay informational.
 */

import { useState } from 'react';
import { ArrowDownToLine, ArrowUpFromLine, Loader2, Trash2, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import { Tabs, TabsContent } from '@/components/ui-kit/tabs';
import {
  ContractsTabsList,
  type ContractsTabsListOption,
} from '@/modules/org/contracts/components/contracts-tabs-list';
import { useToast } from '@/hooks/use-toast';
import {
  pullUilmEnToLocal,
  pushLocalEnToUilm,
} from '../../lib/sync-template-uilm';
import { markKeyAsDeleted } from '../../lib/uilm-existence-cache';
import { deleteUilmKey, generateUilmFile, type UilmKey } from '../../services/uilm-admin.service';
import type { TemplateUilmDiff } from '../../hooks/use-template-uilm-diff';

interface TranslationTabDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  diff: TemplateUilmDiff;
  /** Called after a successful Keep local / Keep UILM so the parent can refetch. */
  onReconciled: () => void;
}

type DetailsTabValue = 'missing' | 'mismatched' | 'orphans' | 'inSync';

export function TranslationTabDetailsDialog({
  open,
  onOpenChange,
  diff,
  onReconciled,
}: TranslationTabDetailsDialogProps) {
  const [activeTab, setActiveTab] = useState<DetailsTabValue>('missing');

  // Tab options share the same shape as the contracts page — first 2
  // visible inline on mobile, the rest fall into a "More" dropdown.
  const tabs: ReadonlyArray<ContractsTabsListOption<DetailsTabValue>> = [
    {
      value: 'missing',
      label: 'Missing',
      badge: <CountBadge count={diff.missing.length} accent="amber" />,
    },
    {
      value: 'mismatched',
      label: 'Mismatches',
      badge: <CountBadge count={diff.mismatched.length} accent="blue" />,
    },
    {
      value: 'orphans',
      label: 'Orphans',
      badge: <CountBadge count={diff.orphans.length} accent="grey" />,
    },
    {
      value: 'inSync',
      label: 'In sync',
      badge: <CountBadge count={diff.inSync.length} accent="green" />,
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] sm:max-w-2xl max-h-[90dvh] overflow-hidden flex flex-col p-4 sm:p-6 rounded-2xl">
        <DialogHeader className="pr-8">
          <DialogTitle className="text-base sm:text-lg">Translation key details</DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            How the local template lines up against UILM right now.
          </DialogDescription>
        </DialogHeader>

        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as DetailsTabValue)}
          className="flex-1 flex flex-col min-h-0"
        >
          <ContractsTabsList<DetailsTabValue>
            options={tabs}
            activeValue={activeTab}
            onSelect={setActiveTab}
            visibleCount={2}
          />

          <TabsContent value="missing" className="flex-1 overflow-y-auto mt-3">
            <KeyList
              emptyLabel="Nothing missing — every local key is on UILM."
              items={diff.missing.map((p) => ({
                keyName: p.keyName,
                rightText: p.enValue,
                badge: p.ownerType,
              }))}
            />
          </TabsContent>

          <TabsContent value="mismatched" className="flex-1 overflow-y-auto mt-3">
            <MismatchedList diff={diff} onReconciled={onReconciled} />
          </TabsContent>

          <TabsContent value="orphans" className="flex-1 overflow-y-auto mt-3">
            <OrphansList diff={diff} onReconciled={onReconciled} />
          </TabsContent>

          <TabsContent value="inSync" className="flex-1 overflow-y-auto mt-3">
            <KeyList
              emptyLabel="No keys are in sync yet — run Seed to push them."
              items={diff.inSync.map((p) => ({
                keyName: p.keyName,
                rightText: p.enValue,
                badge: p.ownerType,
              }))}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Mismatches — interactive list with Keep local / Keep UILM per row
// ---------------------------------------------------------------------------

function MismatchedList({
  diff,
  onReconciled,
}: {
  diff: TemplateUilmDiff;
  onReconciled: () => void;
}) {
  const { toast } = useToast();
  /** Currently-processing key name, or null when idle. Disables both row buttons. */
  const [busyKey, setBusyKey] = useState<string | null>(null);

  if (diff.mismatched.length === 0) {
    return (
      <p className="text-sm text-globe-grey py-8 text-center">
        No value drift — every shared key matches UILM.
      </p>
    );
  }

  const handleKeepLocal = async (mismatch: TemplateUilmDiff['mismatched'][number]) => {
    setBusyKey(mismatch.plan.keyName);
    try {
      await pushLocalEnToUilm(
        diff.moduleId,
        diff.template.slug,
        mismatch.plan.keyName,
        mismatch.plan.enValue,
      );
      toast({
        variant: 'success',
        title: 'Pushed local to UILM',
        description: `"${mismatch.plan.enValue}" is now on UILM.`,
      });
      onReconciled();
    } catch (err) {
      console.error('Keep local failed:', err);
      toast({
        variant: 'destructive',
        title: 'Keep local failed',
        description: err instanceof Error ? err.message : 'Could not push the value to UILM.',
      });
    } finally {
      setBusyKey(null);
    }
  };

  const handleKeepUilm = async (mismatch: TemplateUilmDiff['mismatched'][number]) => {
    setBusyKey(mismatch.plan.keyName);
    try {
      await pullUilmEnToLocal(mismatch.plan, mismatch.remoteEn, diff.template);
      toast({
        variant: 'success',
        title: 'Updated local',
        description: `Local text is now "${mismatch.remoteEn}".`,
      });
      onReconciled();
    } catch (err) {
      console.error('Keep UILM failed:', err);
      toast({
        variant: 'destructive',
        title: 'Keep UILM failed',
        description: err instanceof Error ? err.message : 'Could not update the local record.',
      });
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <ul className="space-y-2">
      {diff.mismatched.map((m) => {
        const isBusy = busyKey === m.plan.keyName;
        const anyBusy = busyKey !== null;
        return (
          <li
            key={m.plan.keyName}
            className="rounded border border-border/60 bg-[hsl(var(--bg))] p-3 space-y-2"
          >
            <div className="flex items-center gap-2 flex-wrap">
              <code className="font-mono text-[10px] text-globe-grey break-all">
                {m.plan.keyName}
              </code>
              <Badge variant="outline" className="text-[9px] text-globe-grey">
                {m.plan.ownerType}
              </Badge>
            </div>

            <div className="grid sm:grid-cols-2 gap-2">
              <ValueCard label="Local" value={m.plan.enValue} accent="amber" />
              <ValueCard label="UILM" value={m.remoteEn} accent="blue" />
            </div>

            <div className="flex items-center gap-2 flex-wrap sm:justify-end pt-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleKeepLocal(m)}
                disabled={anyBusy}
                title="Push the local value up to UILM"
                className="flex-1 sm:flex-initial"
              >
                {isBusy ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <ArrowUpFromLine className="h-3.5 w-3.5 mr-1.5" />
                )}
                Keep local
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleKeepUilm(m)}
                disabled={anyBusy}
                title="Pull the UILM value down to the local record"
                className="flex-1 sm:flex-initial"
              >
                {isBusy ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <ArrowDownToLine className="h-3.5 w-3.5 mr-1.5" />
                )}
                Keep UILM
              </Button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Orphans — interactive list with per-row delete + inline confirmation
// ---------------------------------------------------------------------------

function OrphansList({
  diff,
  onReconciled,
}: {
  diff: TemplateUilmDiff;
  onReconciled: () => void;
}) {
  const { toast } = useToast();
  /** ItemId of the row currently asking "are you sure?". */
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  /** ItemId of the row currently in flight to UILM. */
  const [busyId, setBusyId] = useState<string | null>(null);

  if (diff.orphans.length === 0) {
    return (
      <p className="text-sm text-globe-grey py-8 text-center">No orphan keys on UILM.</p>
    );
  }

  const handleDelete = async (key: UilmKey) => {
    setBusyId(key.itemId);
    try {
      await deleteUilmKey(key.itemId);
      // Republish so the cached file no longer ships the deleted key.
      await generateUilmFile(key.moduleId);
      // Drop from the existence cache so a future re-sync recreates this
      // key cleanly as `isNewKey: true` instead of trying to update it.
      markKeyAsDeleted(key.moduleId, key.keyName);
      toast({
        variant: 'success',
        title: 'Orphan key deleted',
        description: key.keyName,
      });
      setConfirmingId(null);
      onReconciled();
    } catch (err) {
      console.error('Delete failed:', err);
      toast({
        variant: 'destructive',
        title: 'Delete failed',
        description: err instanceof Error ? err.message : 'Could not delete the key.',
      });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <ul className="space-y-2">
      {diff.orphans.map((k) => {
        const enValue = k.resources.find((r) => r.culture === 'en-US')?.value ?? '';
        const isConfirming = confirmingId === k.itemId;
        const isBusy = busyId === k.itemId;
        const anyBusy = busyId !== null;
        return (
          <li
            key={k.itemId}
            className="rounded border border-border/60 bg-[hsl(var(--bg))] p-3 space-y-2"
          >
            <div className="flex items-center gap-2 flex-wrap">
              <code className="font-mono text-[10px] text-globe-grey break-all">
                {k.keyName}
              </code>
            </div>
            {enValue && (
              <div className="text-sm text-[hsl(var(--fg))] wrap-break-word whitespace-pre-wrap">
                {enValue}
              </div>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-end pt-1">
              {isConfirming ? (
                <>
                  <span className="text-xs text-globe-grey sm:mr-2">Delete this key from UILM?</span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setConfirmingId(null)}
                      disabled={isBusy}
                      className="flex-1 sm:flex-initial"
                    >
                      <X className="h-3.5 w-3.5 mr-1.5" />
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(k)}
                      disabled={anyBusy}
                      className="flex-1 sm:flex-initial"
                    >
                      {isBusy ? (
                        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                      )}
                      Confirm delete
                    </Button>
                  </div>
                </>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmingId(k.itemId)}
                  disabled={anyBusy}
                  title="Delete this key from UILM (irreversible)"
                  className="w-full sm:w-auto"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5 text-destructive" />
                  Delete
                </Button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function ValueCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent: 'amber' | 'blue';
}) {
  const accentClass =
    accent === 'amber'
      ? 'border-amber-200 bg-amber-50/40'
      : 'border-selise-blue/20 bg-selise-blue/5';
  return (
    <div className={`rounded border ${accentClass} p-2`}>
      <div className="text-[10px] uppercase tracking-wide text-globe-grey font-medium">
        {label}
      </div>
      <div className="text-sm text-[hsl(var(--fg))] mt-0.5 wrap-break-word whitespace-pre-wrap">
        {value || <span className="italic text-globe-grey">empty</span>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Private — coloured count chip rendered next to each tab label
// ---------------------------------------------------------------------------

type CountAccent = 'amber' | 'blue' | 'grey' | 'green';

const accentBadgeClass: Record<CountAccent, string> = {
  amber: 'text-red-800',
  blue: 'text-selise-blue',
  grey: 'text-globe-grey',
  green: 'text-poly-green',
};

function CountBadge({ count, accent }: { count: number; accent: CountAccent }) {
  return (
    <span className={`text-[10px] sm:text-[12px] font-mono ${accentBadgeClass[accent]}`}>
      {count}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Private — read-only list (Missing / Orphans / In sync)
// ---------------------------------------------------------------------------

interface KeyListItem {
  keyName: string;
  rightText: string;
  badge?: string;
}

function KeyList({ items, emptyLabel }: { items: KeyListItem[]; emptyLabel: string }) {
  if (items.length === 0) {
    return <p className="text-sm text-globe-grey py-8 text-center">{emptyLabel}</p>;
  }
  return (
    <ul className="space-y-1.5">
      {items.map((item) => (
        <li
          key={item.keyName}
          className="text-xs p-2.5 rounded border border-border/60 bg-[hsl(var(--bg))]"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <code className="font-mono text-[10px] text-globe-grey break-all">{item.keyName}</code>
            {item.badge && (
              <Badge variant="outline" className="text-[9px] text-globe-grey">
                {item.badge}
              </Badge>
            )}
          </div>
          {item.rightText && (
            <div className="text-[hsl(var(--fg))] mt-1 line-clamp-2">{item.rightText}</div>
          )}
        </li>
      ))}
    </ul>
  );
}
